import { Outlet, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

// デバイス棚卸しページ共通レイアウト
import { VStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";

import { Header } from "./-components/Header";
import {
  ValidationError,
  getPackageInfo,
  validatePatternId,
  validateRequestId,
} from "./-domain/selectors";
import { useHeader } from "./-hooks/useHeader";

const SearchSchema = z
  .object({
    patternId: z.string(),
    requestId: z.string().optional(),
    packageInstanceId: z.string().optional(),
  })
  .refine(
    (data) =>
      data.requestId !== undefined || data.packageInstanceId !== undefined,
    {
      message: "requestId または packageInstanceId のどちらかが必須です",
    },
  );

export const Route = createFileRoute(
  "/_staff/it-operation-core/device-inventory",
)({
  validateSearch: zodValidator(SearchSchema),
  beforeLoad: async ({ search }) => {
    try {
      // 入力値の検証
      const validatedPatternId = validatePatternId(search.patternId);

      // packageInstanceIdがある場合はrequestIdのバリデーションをスキップ
      // (data.tsxでrequestIdを取得してからURLが更新される)
      const validatedRequestId = search.requestId
        ? validateRequestId(search.requestId)
        : undefined;

      // パッケージ情報を取得
      const packageInfo = await getPackageInfo(validatedPatternId);

      if (!packageInfo) {
        throw new ValidationError("パッケージが見つかりません", "patternId");
      }

      return {
        packageId: packageInfo.packageId,
        packageInfo: packageInfo.packageInfo,
        patternInfo: packageInfo.patternInfo,
        validatedPatternId,
        validatedRequestId,
      };
    } catch (error) {
      // バリデーションエラーの場合は適切なエラーメッセージを表示
      if (error instanceof ValidationError) {
        throw new Error(`入力値エラー: ${error.message}`);
      }

      // その他のエラーは一般的なメッセージに変換
      throw new Error("データの取得に失敗しました。");
    }
  },
  // ローディング中のUIを表示
  pendingComponent: () => <ViewportLoader className="w-full" />,
  component: LayoutComponent,
});

function LayoutComponent() {
  const { patternId, requestId } = Route.useSearch();
  const routeContext = Route.useRouteContext();
  const header = useHeader({ patternId, requestId: requestId ?? "" });

  // コンテキストが利用できない場合のフォールバック
  if (
    !routeContext.packageId ||
    !routeContext.packageInfo ||
    !routeContext.patternInfo
  ) {
    return <ViewportLoader className="w-full" />;
  }

  const { packageId, packageInfo, patternInfo } = routeContext;

  // requestIdがない場合（packageInstanceIdからrequestIdを取得中）
  // Headerは表示せず、Outletのみ表示して子コンポーネントでrequestIdを取得
  if (!requestId) {
    return (
      <VStack>
        <Outlet />
      </VStack>
    );
  }

  return (
    <VStack>
      <Header
        packageId={packageId}
        packageName={packageInfo.name}
        patternName={patternInfo.name}
        patternId={patternId}
        requestId={requestId}
        aggregatedRequestedFor={[]}
        actions={header.actions}
      />
      <Outlet />
    </VStack>
  );
}
